import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router";
import { RequireSession } from "./components/RequireSession";
import { AdminAffiliatePage } from "./pages/admin/AffiliatePage";
import { AdminAffiliatesPage } from "./pages/admin/AffiliatesPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminOverviewPage } from "./pages/admin/OverviewPage";
import { AdminPayoutsPage } from "./pages/admin/PayoutsPage";
import { AdminTermsPage } from "./pages/admin/TermsPage";
import { LinkPage } from "./pages/LinkPage";
import { LinksPage } from "./pages/LinksPage";
import { LoginPage } from "./pages/LoginPage";
import { PromotePage } from "./pages/PromotePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PayoutsPage } from "./pages/PayoutsPage";
import { ReferralsPage } from "./pages/ReferralsPage";
import { TrafficPage } from "./pages/TrafficPage";
import { NotSignedIn } from "./lib/api";
import { detectLocale } from "./i18n";
import "./index.css";

const client = new QueryClient({
  defaultOptions: {
    queries: {
      /*
       * Nothing here polls. Every figure on this dashboard moves when somebody
       * else does something — a stranger clicks a link, a customer tops up, an
       * operator approves a payout — and none of it moves fast enough to be
       * worth a request every few seconds. The customer dashboard polls a
       * running build because a build finishes in a minute; a commission clears
       * in thirty days.
       *
       * A minute of staleness, refetched when the tab comes back, is what makes
       * switching to this tab show current numbers without polling for them.
       */
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      // A signed-out session is not a transient failure and retrying it is
      // three round trips before the redirect the visitor needs.
      retry: (failureCount, error) => !(error instanceof NotSignedIn) && failureCount < 2,
    },
  },
});

/*
 * The language is settled before the first paint rather than switched after
 * it: a page that renders in English and flips to Indonesian a moment later is
 * a flash of the wrong words on every load. `detectLocale` is bounded, so a
 * slow answer costs a moment of English rather than a blank screen.
 */
void detectLocale().finally(() =>
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Everything else is behind the session, decided once rather than per
              route: there is no public page on this dashboard, unlike the
              community's feed. */}
          <Route element={<RequireSession />}>
            <Route index element={<OverviewPage />} />
            <Route path="links" element={<LinksPage />} />
            {/* One campaign. Nested under links rather than given its own
                top-level entry: it is a link, and arriving here means having
                opened one. */}
            <Route path="links/:linkId" element={<LinkPage />} />
            <Route path="promote" element={<PromotePage />} />
            <Route path="traffic" element={<TrafficPage />} />
            <Route path="referrals" element={<ReferralsPage />} />
            <Route path="payouts" element={<PayoutsPage />} />
            {/* Routed for everybody; the API refuses every one of these to
                anybody without the role, which is the boundary. The nav simply
                does not offer them. */}
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminOverviewPage />} />
              <Route path="affiliates" element={<AdminAffiliatesPage />} />
              <Route path="affiliates/:affiliateId" element={<AdminAffiliatePage />} />
              <Route path="payouts" element={<AdminPayoutsPage />} />
              <Route path="terms" element={<AdminTermsPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
),
);
